import { useState, useEffect } from 'react'
import moment from 'moment-wrapper'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'

import DatetimeInputField from './DatetimeInputField'
import TimePicker from './TimePicker'

import { convertToDateFnsFormat, toInternalFakeDate, fromInternalFakeDate } from './utils'

import './react-datepicker.css'

const StyledWrapper = styled.div`
  width: ${props => props.width || '100%'};
`

const isTimeFormat24 = () => {
  return moment.locale().slice(-2) === '24'
}

const DatetimeInput = ({
  value, onChange, isReadOnly, isPositionFixed, isDateOnly, userTimeZone,
  width, placeholder, className,
}) => {
  const newValueDate = toInternalFakeDate(value, userTimeZone)
  const [valueDate, setValueDate] = useState(newValueDate)
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    setValueDate(newValueDate)
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const isTimeFormat24_ = isTimeFormat24()

  const onChange_ = (fakeDate) => {
    const valueMoment = fromInternalFakeDate(fakeDate, userTimeZone)

    setValueDate(fakeDate)
    onChange(valueMoment)
  }

  const getMomentFormat = () => {
    const momentFormats = moment().creationData().locale._longDateFormat
    const momentDateFormat = momentFormats.LL
    const momentTimeFormat = momentFormats.LTS
    const format = isDateOnly ? momentDateFormat : `${momentDateFormat} ${momentTimeFormat}`
    return format
  }

  const onCalendarClose = () => {
    setIsOpen(false)
  }

  const onCalendarOpen = () => {
    setIsOpen(true)
  }

  let displayPlaceholder = placeholder
  if (!displayPlaceholder) {
    displayPlaceholder = isDateOnly ? t('datetimeInput.placeholderDate') : t('datetimeInput.placeholderDateTime')
  }

  const momentFormat = getMomentFormat()
  const datetimeFormat = convertToDateFnsFormat(momentFormat)

  return (
    <StyledWrapper width={width} className={className}>
      <DatePicker
        selected={valueDate}
        onChange={onChange_}
        shouldCloseOnSelect={false}
        placeholderText={displayPlaceholder}
        customInput={
          <DatetimeInputField
            width={width}
            isOpen={isOpen}
            isReadOnly={isReadOnly}
            format={momentFormat}
            userTimeZone={userTimeZone}
          />
        }
        onCalendarClose={onCalendarClose}
        onCalendarOpen={onCalendarOpen}
        dateFormat={datetimeFormat}
        fixedHeight
        // time props
        showTimeInput={!isDateOnly}
        customTimeInput={
          <TimePicker timeFormat24={isTimeFormat24_} />
        }
        timeInputLabel=''
        // popup props
        showPopperArrow={false}
        popperPlacement='bottom-start'
        popperProps={{
          strategy: isPositionFixed ? 'fixed' : 'absolute',
        }}
      />
    </StyledWrapper>
  )
}

DatetimeInput.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.object,
  ]),
  onChange: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  isPositionFixed: PropTypes.bool,
  isDateOnly: PropTypes.bool,
  userTimeZone: PropTypes.string.isRequired,
  width: PropTypes.string,
  placeholder: PropTypes.string,
}

export default DatetimeInput
